import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, PLATFORM_ID, Renderer2, SimpleChanges } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, filter, share, startWith, Subject, takeUntil, distinctUntilChanged, map } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormControl } from '@angular/forms';
import { AcvisService } from '@app/services/acvis.service';
import { MEDIA_TOKEN } from '../../../../../../tockens';

interface Item {
    id: string;
    code?: string;
    name: string;
    vc?: string;
    selected?: boolean;
}

@Component({
    selector: 'clever-searchlist',
    templateUrl: './searchlist.component.html',
    styleUrls: ['./searchlist.component.scss'],
    animations: [
        trigger('zoom', [
            state('leftBottom', style({ transform: 'scale(0.3)', transformOrigin: 'left bottom', opacity: '0' })),
            state('default', style({ transform: 'scale(1)', opacity: '1' })),
            transition('* <=> default', animate('10ms cubic-bezier(0.25,1,.25,1)'))
        ])
    ],
    standalone: false
})
export class SearchlistComponent implements OnInit, OnDestroy, OnChanges {
    @Input() mode: string;
    @Input() title: string = '';
    @Input() icon: string = '';
    @Input() list: any[] = [];
    @Input() listpriority: any[] = [];
    @Input() showVc: boolean = false;
    @Input() bottomleftradius: boolean = false;
    @Input() bottomrightradius: boolean = false;
    @Input() topleftradius: boolean = false;
    @Input() toprightradius: boolean = false;
    @Input() norightborder: boolean = false;
    @Input() nobottomborder: boolean = false;
    @Output() sendSelection: EventEmitter<any> = new EventEmitter();
    protected unsubscribe$ = new Subject();
    isBrowser: boolean = false;
    showModal: boolean = false;
    focus: boolean = true;
    media: string = 'lg';
    origin = 'default';
    zielText = '';
    searchObj: Observable<any>;
    mySearchControl = new FormControl();
    selectedList: Item[] = [];

    constructor(
        protected mediaObserver: BreakpointObserver,
        protected cdr: ChangeDetectorRef,
        protected acvisService: AcvisService,
        protected render: Renderer2,
        @Inject(PLATFORM_ID) protected platformId: any,
        @Optional() @Inject(MEDIA_TOKEN) private media_token: string
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.media = this.media_token;
    }

    ngOnInit(): void {
        this.zielText = this.title;
        this.initMedia();
        this.initSearchChange();
        this.checkSelectedList();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['list']?.isFirstChange() && changes['list']?.currentValue) {
            this.list = changes['list'].currentValue;
            this.checkSelectedList();
            this.cdr.detectChanges();
        }
    }

    checkSelectedList(): void {
        this.selectedList = [];
        if (this.list) {
            for (let i = 0; i < this.list.length; i++) {
                if (this.list[i].selected) {
                    this.selectedList.push({
                        name: this.list[i].name,
                        id: this.list[i].id,
                        code: this.list[i].id,
                    });
                }
            }
            if (this.selectedList?.length > 0) {
                this.zielText = this.selectedList.length + ' ' + this.title;
            } else {
                this.zielText = this.title;
            }
            this.cdr.detectChanges();
        }
    }

    initSearchChange(): void {
        const filterShare = this.mySearchControl.valueChanges.pipe(
            startWith(''),
            filter(value => (typeof value === 'string')),
            share()
        );

        this.searchObj = filterShare.pipe(
            distinctUntilChanged(),
            startWith(''),
            map(value => this.filterInList(value, this.list))
        );
    }

    filterInList(value: string, array: any[]): any[] {
        const filterValue = value.toLowerCase();
        if (filterValue?.length > 0) {
            return array.filter((option) => {
                return (option.name).toLowerCase().indexOf(filterValue) >= 0;
            }).slice(0, 50);
        } else {
            return this.listpriority?.length > 0 ? this.listpriority : array.slice(0, 50);
        }
    }

    initMedia(): void {
       if (this.mediaObserver.isMatched(Breakpoints.XSmall)) {
            this.media = 'xs';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Small)) {
            this.media = 'sm';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Medium)) {
            this.media = 'md';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Large)) {
            this.media = 'lg';
        }
        if (this.mediaObserver.isMatched(Breakpoints.XLarge)) {
            this.media = 'xl';
        }
        this.cdr.detectChanges();
    }

    
    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    openModal(ev): void {
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    openModalMobile(ev): void {
        // this.render.addClass(document.body, 'model-open');
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    closeModal(): void {
        this.showModal = false;
        this.mySearchControl.setValue('');
        this.cdr.detectChanges();
    }

    closeModalMobile(): void {
        this.showModal = false;
        // this.render.removeClass(document.body, 'model-open');
        this.mySearchControl.setValue('');
        this.cdr.detectChanges();
    }

    zoomin(position: any): void {
        this.origin = position;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.origin = 'default';
            this.cdr.detectChanges();
        });
    }

    clearText(): void {
        this.mySearchControl.setValue('');
        this.focus = false;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.focus = true;
            this.cdr.detectChanges();
        }, 50);
    }

    selectValue(item): void {
        this.selectedList.push({
            name: item.name,
            id: item.id,
            code: item.id,
        });
        this.zielText = this.selectedList.length + ' ' + this.title;
    }

    isNotInlist(item): boolean {
        for (let i = 0; i < this.selectedList.length; i++) {
            if (this.selectedList[i].id === item?.id) {
                return false;
            }
        }
        return true;
    }

    removeAllSelected(): void {
        this.selectedList = [];
        this.zielText = this.title;
    }

    removeFromSelected(k): void {
        if (this.selectedList[k]) {
            this.selectedList.splice(k, 1);
        }
        this.zielText = this.selectedList.length > 0 ? this.selectedList.length + '' : this.title;
    }

    take(): void {
        this.sendSelection.emit(JSON.parse(JSON.stringify(this.selectedList)));
        this.closeModal();
    }
}
